import { useCallback, useMemo } from 'react';
import { LayoutGrid, Plus } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { Filters, Header, IconHighlight, ViewToggle } from '@aimsos/ui';

/** Stable reference — `Filters` is memoised, so an inline array re-renders it every time. */
const NO_FILTER_SLOTS = [];

/**
 * The page's sticky band: header + the search/view row that folds away under it.
 *
 * Replaces a bespoke mechanism — an `IntersectionObserver` on a sentinel, a
 * `ResizeObserver` + `requestAnimationFrame` loop keeping a `position: fixed`
 * bar pixel-aligned to the scroll container, and an app-local
 * `--header-compact-fade` CSS variable for its background. That variable is
 * declared in `apps/workspaces/src/index.css`, which is not loaded when the app
 * runs embedded in Core Shell, so the bar rendered fully transparent and the
 * workspace cards scrolled straight through it.
 *
 * `position: sticky` does all of that natively. Scroll state comes from the page
 * (`useStickyScroll`), so this component stays presentational.
 *
 * See `docs/architecture/list-page/`.
 */
const WorkspacesListHeader = ({
  searchValue,
  onSearchChange,
  onSearchClear,
  view,
  onViewChange,
  onCreateClick,
  isScrolled = false,
  showToolbar = true,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { t } = useTranslation('workspaces');
  const handleSearchInputChange = useCallback(
    (e) => {
      onSearchChange?.(e.target.value);
    },
    [onSearchChange],
  );

  const searchProps = useMemo(
    () => ({
      value: searchValue,
      onChange: handleSearchInputChange,
      onClear: onSearchClear,
      placeholder: t('list.searchPlaceholder'),
    }),
    [searchValue, handleSearchInputChange, onSearchClear, t],
  );

  return (
    <div
      className="sticky top-0 z-20 px-6"
      onMouseEnter={isScrolled ? onMouseEnter : undefined}
      onMouseLeave={isScrolled ? onMouseLeave : undefined}
    >
      {/* Vertical padding on the PAINTED element, never the sticky wrapper —
          otherwise the backdrop stops at the header's own box and cards scroll
          visibly through the gutter above and below it. */}
      <div
        className={['pb-6 pt-6 transition-colors duration-200', isScrolled ? 'bg-surface-base' : '']
          .filter(Boolean)
          .join(' ')}
      >
        <Header
          size={isScrolled ? 'compress' : 'M'}
          showIcon
          icon={<IconHighlight icon={LayoutGrid} bgColor="Primary" iconColor="PrimaryAlt" size="small" />}
          title={t('list.title')}
          showBackButton={false}
          showDescription={!isScrolled}
          description={t('list.subtitle')}
          showTag={false}
          // `size: 'md'` on purpose — at header size `M` the CTA would inherit
          // `sm` and come out smaller than every other list page's.
          primaryCta={{
            label: t('list.createButton'),
            onClick: onCreateClick,
            leftIcon: Plus,
            size: 'md',
          }}
        />

        {/* Folded by CSS rather than unmounted, so the fold has something to
            animate from and the search term survives. `inert` keeps the hidden
            row out of the tab order. */}
        <div
          inert={showToolbar ? undefined : ''}
          className={[
            'transition-all duration-200',
            showToolbar
              ? 'mt-4 max-h-40 overflow-visible opacity-100'
              : 'mt-0 max-h-0 overflow-hidden opacity-0',
          ].join(' ')}
        >
          {/* The design-system `Filters` bar — the same component every other
              list page uses. This page narrows on search alone, so it carries no
              filter slots and no "All filters" button.

              The view toggle sits BESIDE the bar rather than in its `viewToggle`
              slot: `Filters` renders sort / viewToggle / actions inside
              `FILTERS_RIGHT_DESKTOP_ONLY`, so below 1280px they disappear with
              nowhere to go. That is the right call for filters — they fall back
              to the "All filters" slideout — but a display mode is not a filter
              and has no slideout to fall back to, so in the bar's own slot the
              toggle would simply be unreachable on a narrower screen. */}
          <div className="flex items-center justify-between gap-3">
            <Filters
              className="min-w-0 flex-1"
              searchProps={searchProps}
              filters={NO_FILTER_SLOTS}
              sort={null}
              showAllFilters={false}
            />
            <ViewToggle view={view} onChange={onViewChange} />
          </div>
        </div>
      </div>

      {/* Cards have to leave somewhere, or they reappear at full opacity a pixel
          under a hard edge. */}
      {isScrolled ? (
        <div
          aria-hidden
          className="pointer-events-none h-10 bg-gradient-to-b from-surface-base to-transparent"
        />
      ) : null}
    </div>
  );
};

export default WorkspacesListHeader;
